import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import CampaignIcon from "@mui/icons-material/Campaign";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CasinoIcon from "@mui/icons-material/Casino";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import GridView from "@mui/icons-material/GridView";
import LaunchIcon from "@mui/icons-material/Launch";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import SpeakerNotesOffIcon from "@mui/icons-material/SpeakerNotesOff";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Alert,
	Badge,
	Box,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Chip,
	IconButton,
	Menu,
	MenuItem,
	MenuList,
	Snackbar,
	Stack,
	Tooltip,
	useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
	GaugeContainer,
	GaugeReferenceArc,
	GaugeValueArc,
	useGaugeState,
} from "@mui/x-charts/Gauge";
import { isPast } from "date-fns";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link as RRLink, useNavigate } from "react-router-dom";
import {
	Assignment,
	type AssignmentJobAd,
	AssignmentStatus,
	cloneAssignment,
	createAssignment,
	DEFAULT_ASSIGNMENT_STATUS_FILTER,
	deleteAssignment,
	getAssignmentById,
	getAssignments,
	getRandomDescription,
	patchAssignmentById,
	statusKeysToValues,
	statusToThemeKey,
	User,
} from "../../api";
import {
	absoluteAdUrl,
	JOB_AD_CHANGED_EVENT,
	type JobAdChangedDetail,
} from "../../api/assignments/job-ad";
import { JobylonLinkedJob } from "../../api/assignments/jobylon-jobs";
import {
	AssignmentLlmQueueStat,
	getAssignmentLlmQueueStats,
} from "../../api/assignments/llm-queue-stats";
import {
	AssignmentVoteCounts,
	getAssignmentVoteCountsBulk,
} from "../../api/assignments/vote-counts";
import {
	CelTask,
	filterRelevantTasks,
	getRunningTasksByAssignment,
} from "../../api/celtask";
import {
	countDueRemindersByAssignment,
	fetchDueReminders,
} from "../../api/pitch/pitch-reminder";
import {
	CommsMatrixStats,
	getCommsMatrixStatsBulk,
} from "../../api/statistics/communicationmatrix";
import {
	JOBYLON_LINK_CHANGED_EVENT,
	JobylonLinkChangedDetail,
	useJobylonLinkedJobs,
} from "../../hooks/use-jobylon-linked-jobs";
import { useRefreshHotkey } from "../../hooks/use-refresh-hotkey";
import { useStatusChoices } from "../../hooks/use-status-choices";
import { getDisplayCompanyName } from "../../utils/privacy";
import AssignmentAddDialog from "../assignmentsv2/AssignmentAddDialog";
import AssignmentsList from "../assignmentsv2/AssignmentsList";
import AssignmentViewModal from "../assignmentsv2/AssignmentViewModal";
import { uniqueName } from "../assignmentsv2/util";
import { useGlobalState } from "../globalState";
import { JobylonBadge } from "../shared/jobylon-badge";
import { RunningTasksChip } from "./RunningTasksChip";

const SavedJobAdPreviewDialog = lazy(() =>
	import("../assignmentsv2/job-ad-preview-dialog").then((m) => ({
		default: m.SavedJobAdPreviewDialog,
	}))
);

const GRADE_METHOD_OPTIONS: { key: string; label: string }[] = [
	{ key: "ml", label: "ML" },
	{ key: "search", label: "SEARCH" },
	{ key: "llm_yes", label: "LLM YES" },
	{ key: "llm_no", label: "LLM NO" },
];

const GaugePointer = () => {
	const theme = useTheme();
	const { valueAngle, outerRadius, cx, cy } = useGaugeState();

	if (valueAngle === null) {
		return null;
	}

	const target = {
		x: cx + outerRadius * Math.sin(valueAngle),
		y: cy - outerRadius * Math.cos(valueAngle),
	};
	return (
		<g>
			<circle cx={cx} cy={cy} r={3} fill={theme.actionButtons.thumbDown.main} />
			<path
				d={`M ${cx} ${cy} L ${target.x} ${target.y}`}
				stroke={theme.actionButtons.thumbDown.main}
				strokeWidth={2}
			/>
		</g>
	);
};

const KpiBadge = ({
	value,
	tooltipTitle,
	tooltipBody,
	onClick,
	to,
	icon,
	warn = false,
}: {
	value: number;
	tooltipTitle: string;
	tooltipBody?: React.ReactNode;
	onClick?: () => void;
	to?: string;
	/** Drawn to the left of the count. Badges without one are bare numbers,
	 *  told apart by their position in the row. */
	icon?: React.ReactNode;
	/** Tints the count, for a badge whose number is something to act on. */
	warn?: boolean;
}) => {
	function formatNumber(num: number): string {
		if (num === undefined || num === null) return "N/A";
		if (num < 1000) {
			return num.toString(); // Less than 1000, return the number as is
		} else if (num < 1000000) {
			return (Math.round(num / 100) / 10).toFixed(0) + "K"; // Thousands
		} else {
			return (Math.round(num / 100000) / 10).toFixed(0) + "M"; // Millions
		}
	}

	return (
		<Tooltip
			data-component="kpi-tooltip"
			title={
				value !== undefined && value !== null ? (
					<Stack spacing={2} data-component="tooltip-content-stack">
						<Typography data-component="tooltip-title">
							{tooltipTitle} ({value})
						</Typography>
						{tooltipBody}
					</Stack>
				) : (
					<Typography data-component="tooltip-loading">
						{tooltipTitle} Loading...
					</Typography>
				)
			}
		>
			<Stack spacing={1} alignItems="center" data-component="kpi-badge-stack">
				<Chip
					data-component="kpi-chip"
					sx={{
						background: (theme) => theme.palette.background.paper,
						cursor: onClick || to ? "pointer" : undefined,
					}}
					size="small"
					clickable={Boolean(onClick || to)}
					onClick={onClick}
					{...(to ? { component: RRLink, to } : {})}
					label={
						value !== undefined && value !== null ? (
							icon ? (
								<Stack
									direction="row"
									spacing={0.5}
									alignItems="center"
									data-component="kpi-icon-value-stack"
								>
									{icon}
									<Typography
										data-component="kpi-value"
										variant="body2"
										fontWeight="bold"
										color={warn ? "warning.main" : "primary"}
									>
										{formatNumber(value)}
									</Typography>
								</Stack>
							) : (
								<Typography
									data-component="kpi-value"
									variant="body2"
									fontWeight="bold"
									color="primary"
								>
									{formatNumber(value)}
								</Typography>
							)
						) : (
							<Typography
								data-component="kpi-placeholder"
								variant="body2"
								fontWeight="bold"
								sx={{
									color: (theme) => theme.palette.background.paper,
								}}
							>
								{0}
							</Typography>
						)
					}
				/>
			</Stack>
		</Tooltip>
	);
};

/** This role's own ad on our job board. Sits beside External url, which is the
 *  client's own posting and a different link entirely.
 *
 *  Published: filled icon, opens the live page. Draft: outlined icon, opens
 *  the same preview the Job ad wizard shows — the public page serves published
 *  ads only, so there is nothing to link to yet. */
const JobAdMark = ({
	jobAd,
	assignmentId,
}: {
	jobAd: AssignmentJobAd;
	assignmentId: number;
}) => {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	if (!jobAd.is_published)
		return (
			<>
				<Tooltip
					title="Job ad not published yet — preview it"
					data-component="job-ad-draft-tooltip"
				>
					<IconButton
						data-component="job-ad-draft-button"
						size="small"
						onClick={() => setIsPreviewOpen(true)}
						sx={{ color: "text.disabled" }}
					>
						<CampaignOutlinedIcon />
					</IconButton>
				</Tooltip>
				<Suspense fallback={null}>
					{isPreviewOpen && (
						<SavedJobAdPreviewDialog
							open
							onClose={() => setIsPreviewOpen(false)}
							assignmentId={assignmentId}
						/>
					)}
				</Suspense>
			</>
		);
	return (
		<Tooltip title="Open the published job ad" data-component="job-ad-tooltip">
			<IconButton
				data-component="job-ad-button"
				size="small"
				href={absoluteAdUrl(jobAd.url)}
				target="_blank"
			>
				<CampaignIcon />
			</IconButton>
		</Tooltip>
	);
};

type RunningTasksMap = Record<number, CelTask[]>;

/** The reminder entry point on a role card: candidates this role contacted with
 *  a personally drafted message who never answered, and how many. Shown only
 *  when there are any — a role whose candidates all replied has nothing to
 *  nudge, and an always-present zero would train the eye to ignore it. */
const ReminderIconButton = ({
	assignmentId,
	count,
}: {
	assignmentId: number;
	count: number;
}) => (
	<Tooltip
		title={`${count} personally contacted ${
			count === 1 ? "candidate waits" : "candidates wait"
		} for a follow-up reminder`}
		data-component="card-reminder-tooltip"
	>
		<IconButton
			data-component="card-reminder-button"
			color="primary"
			size="small"
			component={RRLink}
			to={`/grading/${assignmentId}?method=reminders`}
		>
			<Badge
				data-component="card-reminder-badge"
				badgeContent={count}
				color="warning"
			>
				<ScheduleSendIcon />
			</Badge>
		</IconButton>
	</Tooltip>
);

const AssignmentComponent = ({
	readOnly,
	assignment,
	activeUser,
	onSelectAssignment,
	runningTasks = null,
	onTasksChanged,
	llmQueueCount = 0,
	commsMatrixStats,
	voteCounts,
	reminderDueCount = 0,
	jobylonBoardUrl,
}: {
	readOnly: boolean;
	assignment: Assignment;
	activeUser: User;
	onSelectAssignment?: (assignment: Assignment) => void;
	runningTasks?: RunningTasksMap | null;
	onTasksChanged: () => void;
	llmQueueCount?: number;
	commsMatrixStats?: CommsMatrixStats;
	voteCounts?: AssignmentVoteCounts;
	/** Candidates of this role owed a reminder after an unanswered personal
	 *  pitch. Zero hides the icon. */
	reminderDueCount?: number;
	/** This role's Jobylon candidate board, when it came from Jobylon at all. */
	jobylonBoardUrl?: string;
}) => {
	const { currentUser, users } = useGlobalState();
	const navigate = useNavigate();
	const stats: CommsMatrixStats = commsMatrixStats ?? {
		sent: null,
		responses: null,
		interested: null,
		stale: null,
	};
	const [assignmentWarning, setAssignmentWarning] = useState<{
		text: string;
		icon: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	}>({ text: "", icon: null });

	const [thumbMenuPosition, setThumbMenuPosition] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);

	const handleOpenThumbContextMenu = (event: React.MouseEvent) => {
		event.preventDefault();
		setThumbMenuPosition({
			mouseX: event.clientX + 2,
			mouseY: event.clientY - 6,
		});
	};

	const handleCloseThumbContextMenu = () => setThumbMenuPosition(null);

	const handleSelectGradeMethod = (method: string) => {
		navigate(`/grading/${assignment.id}?method=${method}`);
		setThumbMenuPosition(null);
	};

	useEffect(() => {
		validateCard();
	}, [assignment]);

	const validateCard = () => {
		if (!assignment.submission_deadline) return;

		let text = "";
		let icon = null;
		const twoDaysInSeconds = 86400000 * 2;

		if (isPast(new Date(assignment.submission_deadline))) {
			text = "Submission deadline surpassed";
			icon = <ErrorOutlineIcon color={"error"} />;
		} else if (
			new Date(new Date().getTime() + twoDaysInSeconds) >=
			new Date(assignment.submission_deadline)
		) {
			text = "Submission deadline is soon";
			icon = <WarningAmberIcon color={"warning"} />;
		}
		setAssignmentWarning({ text, icon });
	};

	const secondaryValue = () => {
		const href = `/grading/${assignment.id}?method=llm`;
		const board_href = `/board/v2?assignmentId=${assignment.id}`;
		const stats_href = `/assignment_stats/${assignment.id}`;

		return (
			<>
				{assignment.users.includes(currentUser.id) ||
				assignment.thumbers.includes(currentUser.id) ||
				currentUser.is_staff ? (
					<Tooltip title="Vote" data-component="vote-tooltip">
						<IconButton
							data-component="vote-button"
							color="primary"
							size="small"
							component={RRLink}
							to={href}
							onContextMenu={handleOpenThumbContextMenu}
						>
							<ThumbUpOffAltIcon />
						</IconButton>
					</Tooltip>
				) : null}

				{/* Beside the thumb, because it is the same job on the same
				    candidates — reach the ones this role already wrote to. */}
				{reminderDueCount > 0 && (
					<ReminderIconButton
						assignmentId={assignment.id}
						count={reminderDueCount}
					/>
				)}

				{/* A Jobylon role's candidates live on the customer's own board,
				    so ODI offers none of its own — the wordmark in the header is
				    the way there. */}
				{!jobylonBoardUrl && (
					<Tooltip title="Board" data-component="board-tooltip">
						<IconButton
							data-component="board-button"
							size="small"
							component={RRLink}
							to={board_href}
							color="primary"
						>
							<ListAltIcon />
						</IconButton>
					</Tooltip>
				)}
				<Tooltip title="Pipeline" data-component="pipeline-tooltip">
					<IconButton
						data-component="pipeline-button"
						size="small"
						component={RRLink}
						to={`/funnel/assignment/${assignment.id}`}
						color="primary"
					>
						<FilterListIcon />
					</IconButton>
				</Tooltip>
				{currentUser.is_superuser || currentUser.is_staff ? (
					<Tooltip title="Statistics" data-component="stats-tooltip">
						<IconButton
							data-component="stats-button"
							size="small"
							component={RRLink}
							to={stats_href}
							color="primary"
						>
							<BarChartIcon />
						</IconButton>
					</Tooltip>
				) : null}
				{currentUser.is_staff && (
					<Tooltip
						title="Candidates Pipeline"
						data-component="candidates-tooltip"
					>
						<IconButton
							data-component="candidates-button"
							size="small"
							component={RRLink}
							to={`/candidates/${assignment.id}`}
							color="primary"
						>
							<AccountTreeIcon />
						</IconButton>
					</Tooltip>
				)}
			</>
		);
	};

	const statsSection = () => {
		return (
			<>
				<Grid data-component="votes-grid">
					<KpiBadge
						value={voteCounts?.total}
						tooltipTitle="Number of Votes"
						to={`/funnel/assignment/${assignment.id}`}
					/>
				</Grid>
				<Grid data-component="upvotes-grid">
					<KpiBadge
						value={voteCounts?.up}
						tooltipTitle="Number of Upvotes"
						to={`/funnel/assignment/${assignment.id}?filter=upvotes`}
					/>
				</Grid>
				<Grid data-component="sent-grid">
					<KpiBadge
						value={stats.sent}
						tooltipTitle="Sent Messages"
						to={`/funnel/assignment/${assignment.id}?filter=sent`}
					/>
				</Grid>
				<Grid data-component="responses-grid">
					<KpiBadge
						value={stats.responses}
						tooltipTitle="Received Answers"
						to={`/funnel/assignment/${assignment.id}?filter=responses`}
					/>
				</Grid>
				<Grid data-component="interested-grid">
					<KpiBadge
						value={stats.interested}
						tooltipTitle="Interested candidates"
						to={`/funnel/assignment/${assignment.id}?filter=interested`}
					/>
				</Grid>
				<Grid data-component="stale-grid">
					<KpiBadge
						value={stats.stale}
						tooltipTitle="Stale conversations"
						tooltipBody={
							<Typography
								data-component="stale-tooltip-body"
								variant="body2"
							>
								They answered us, we answered them, and nothing has come
								back since.
							</Typography>
						}
						warn={stats.stale > 0}
						icon={
							<SpeakerNotesOffIcon
								fontSize="small"
								data-component="stale-icon"
								sx={{
									color: (theme) =>
										stats.stale > 0
											? theme.palette.warning.main
											: theme.palette.text.secondary,
								}}
							/>
						}
						to={`/funnel/assignment/${assignment.id}?filter=stale`}
					/>
				</Grid>
			</>
		);
	};

	const theme = useTheme();
	let cardTitle;
	let cardSubheader;

	if (assignment.is_recruiting_company) {
		cardTitle = (
			<Stack data-component="company-title-stack">
				<Typography color="text.primary" data-component="company-name">
					{assignment.company != null
						? getDisplayCompanyName(
								assignment.company_name,
								assignment.company,
								currentUser.userprofile
							)
						: ""}
				</Typography>
			</Stack>
		);
		cardSubheader = (
			<Link
				data-component="assignment-link"
				onClick={() => onSelectAssignment(assignment)}
				sx={{ cursor: "pointer" }}
			>
				<Typography
					variant="body2"
					color="text.primary"
					data-component="assignment-name"
				>
					{assignment.name} ({assignment.id})
				</Typography>
			</Link>
		);
	} else {
		cardTitle = (
			<Link
				data-component="assignment-title-link"
				onClick={() => onSelectAssignment(assignment)}
				sx={{ cursor: "pointer" }}
			>
				<Typography color="text.primary" data-component="assignment-title">
					{assignment.name} ({assignment.id})
				</Typography>
			</Link>
		);
		cardSubheader = null;
	}

	return (
		<Box data-component="assignment-grid" sx={{ display: "grid" }}>
			<Card
				data-component="assignment-card"
				data-assignment-id={assignment.id}
				sx={{
					backgroundColor: theme.palette.background.default,
					position: "relative",
				}}
			>
				<CardHeader
					data-component="assignment-header"
					sx={{ p: 1 }}
					title={cardTitle}
					subheader={cardSubheader}
					action={
						jobylonBoardUrl ||
						assignment.external_url ||
						assignment.job_ad ? (
							<Stack
								direction="row"
								alignItems="center"
								spacing={0.5}
								data-component="assignment-card-marks"
							>
								{jobylonBoardUrl && (
									// 20px is what a small IconButton renders its
									// icon at, so the wordmark sits level with the
									// external-link button beside it.
									<JobylonBadge
										height={20}
										href={jobylonBoardUrl}
										title="Open this role's candidates in Jobylon"
									/>
								)}
								{assignment.job_ad ? (
									<JobAdMark
										jobAd={assignment.job_ad}
										assignmentId={assignment.id}
									/>
								) : null}
								{assignment.external_url ? (
									<Tooltip
										title="External link"
										data-component="external-link-tooltip"
									>
										<IconButton
											data-component="external-link-button"
											size="small"
											href={assignment.external_url}
											target="_blank"
										>
											<LaunchIcon />
										</IconButton>
									</Tooltip>
								) : null}
							</Stack>
						) : null
					}
				/>
				<CardContent sx={{ p: 1 }} data-component="assignment-content">
					<Grid
						container
						spacing={1}
						alignItems="center"
						data-component="assignment-details"
					>
						<Grid data-component="status-grid">
							<Chip
								data-component="status-chip"
								label={assignment.status_display}
								size="small"
								sx={{
									backgroundColor:
										theme.assignmentStatus[
											statusToThemeKey[
												AssignmentStatus[assignment.status]
											]
										],
									color: "common.black",
								}}
							/>
							{!assignment.users.includes(currentUser.id) &&
								assignment.thumbers.includes(currentUser.id) && (
									<Tooltip
										title={`Screener only — outreach is handled by ${
											assignment.users
												.map(
													(id) =>
														users?.find((u) => u.id === id)
															?.first_name
												)
												.filter(Boolean)
												.join(", ") || "unknown"
										}`}
									>
										<Chip
											data-component="screener-chip"
											label="Screener"
											size="small"
											color="info"
											variant="outlined"
											sx={{ ml: 0.5 }}
										/>
									</Tooltip>
								)}
							{assignment.egenkonsult && (
								<Chip
									data-component="assignment-card-freelancer"
									label="Freelancer"
									size="small"
									color="secondary"
									variant="filled"
									sx={{ ml: 0.5 }}
								/>
							)}
						</Grid>
						<Grid data-component="celery-grid">
							{(() => {
								const allTasks = runningTasks?.[assignment.id];
								if (!allTasks || allTasks.length === 0) return null;

								const relevantTasks = filterRelevantTasks(allTasks);
								if (relevantTasks.length === 0) return null;

								return (
									<RunningTasksChip
										tasks={relevantTasks}
										canCancel={
											currentUser.is_staff ||
											currentUser.is_superuser
										}
										onCancelled={onTasksChanged}
									/>
								);
							})()}
						</Grid>
						<Grid data-component="warning-grid">
							{assignmentWarning.text !== "" ? (
								<Tooltip
									data-component="warning-tooltip"
									title={`${assignmentWarning.text}: ${assignment.submission_deadline}`}
								>
									<IconButton
										data-component="warning-button"
										size="small"
										onClick={() => onSelectAssignment(assignment)}
									>
										{assignmentWarning.icon}
									</IconButton>
								</Tooltip>
							) : null}
						</Grid>
						<Grid size={12} data-component="stats-container">
							<Grid
								container
								spacing={1}
								mt={2}
								data-component="stats-grid"
							>
								{statsSection()}
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
				<CardActions data-component="card-actions">
					{secondaryValue()}
				</CardActions>
				<Tooltip title="Candidates in Queue" data-component="llm-gauge-tooltip">
					<Box
						data-component="llm-gauge-container"
						data-llm-queue-count={llmQueueCount}
						sx={{
							position: "absolute",
							bottom: 4,
							right: 4,
							width: 60,
							height: 60,
						}}
					>
						<GaugeContainer
							width={60}
							height={60}
							startAngle={-110}
							endAngle={110}
							value={Math.min(llmQueueCount, 100)}
							valueMin={0}
							valueMax={100}
						>
							<GaugeReferenceArc />
							<GaugeValueArc />
							<GaugePointer />
						</GaugeContainer>
					</Box>
				</Tooltip>
				<Menu
					data-component="context-menu"
					open={thumbMenuPosition !== null}
					onClose={handleCloseThumbContextMenu}
					anchorReference="anchorPosition"
					anchorPosition={
						thumbMenuPosition !== null
							? {
									top: thumbMenuPosition.mouseY,
									left: thumbMenuPosition.mouseX,
								}
							: undefined
					}
				>
					<MenuList dense data-component="menu-list">
						{GRADE_METHOD_OPTIONS.map((opt) => (
							<MenuItem
								data-component="grade-method-item"
								key={`grade-method-${opt.key}`}
								onClick={() => handleSelectGradeMethod(opt.key)}
							>
								{opt.label}
							</MenuItem>
						))}
					</MenuList>
				</Menu>
			</Card>
		</Box>
	);
};

const RecruitmentAssignmentList = ({
	assignments,
	readOnly = false,
	activeUser,
	onSelectAssignment,
	onAddAssignment,
	onAddRandomAssignment,
	runningTasks = null,
	onTasksChanged,
	llmQueueStats = [],
	commsMatrixStatsMap = {},
	voteCountsMap = {},
	reminderDueMap = {},
	jobylonByAssignment,
}: {
	assignments: Assignment[];
	connected?: boolean;
	readOnly?: boolean;
	activeUser: User;
	onSelectAssignment?: (assignment: Assignment) => void;
	onAddAssignment?: () => void;
	onAddRandomAssignment?: () => void;
	runningTasks?: RunningTasksMap | null;
	onTasksChanged: () => void;
	llmQueueStats?: AssignmentLlmQueueStat[];
	commsMatrixStatsMap?: Record<number, CommsMatrixStats>;
	voteCountsMap?: Record<number, AssignmentVoteCounts>;
	/** Due personal-pitch reminders per assignment id. Missing means none. */
	reminderDueMap?: Record<number, number>;
	/** Jobylon job per assignment id. Empty for customers without it. */
	jobylonByAssignment: Map<number, JobylonLinkedJob>;
}) => {
	const theme = useTheme();
	const llmQueueMap = llmQueueStats.reduce(
		(acc, stat) => {
			acc[stat.assignment_id] = stat.llm_queue_count;
			return acc;
		},
		{} as Record<number, number>
	);

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
				gap: 2,
				pb: 2,
				px: 2,
			}}
			data-component="assignment-list"
		>
			{onAddAssignment && (
				<Box data-component="add-assignment-grid" sx={{ display: "grid" }}>
					<Card
						data-component="add-assignment-card"
						onClick={onAddAssignment}
						sx={{
							border: `2px dashed ${theme.palette.divider}`,
							backgroundColor: "transparent",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							minHeight: 180,
							"&:hover": {
								borderColor: theme.palette.primary.main,
								backgroundColor: theme.palette.action.hover,
							},
						}}
					>
						<Stack alignItems="center" spacing={1}>
							<AddIcon
								sx={{
									fontSize: 40,
									color: theme.palette.text.secondary,
								}}
							/>
							<Typography variant="body2" color="text.secondary">
								Add new Role
							</Typography>
						</Stack>
					</Card>
				</Box>
			)}
			{onAddRandomAssignment &&
				(activeUser.is_staff || activeUser.is_superuser) && (
					<Box
						data-component="add-random-assignment-grid"
						sx={{ display: "grid" }}
					>
						<Card
							data-component="add-random-role-card"
							id="assignments-add-random-role-button"
							onClick={onAddRandomAssignment}
							sx={{
								border: `2px dashed ${theme.palette.divider}`,
								backgroundColor: "transparent",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								cursor: "pointer",
								minHeight: 180,
								"&:hover": {
									borderColor: theme.palette.primary.main,
									backgroundColor: theme.palette.action.hover,
								},
							}}
						>
							<Stack alignItems="center" spacing={1}>
								<CasinoIcon
									sx={{
										fontSize: 40,
										color: theme.palette.text.secondary,
									}}
								/>
								<Typography variant="body2" color="text.secondary">
									Add random Role
								</Typography>
							</Stack>
						</Card>
					</Box>
				)}
			{assignments.map((assignment) => {
				return (
					<AssignmentComponent
						key={assignment.id}
						readOnly={readOnly}
						activeUser={activeUser}
						assignment={assignment}
						runningTasks={runningTasks}
						onTasksChanged={onTasksChanged}
						onSelectAssignment={onSelectAssignment}
						llmQueueCount={llmQueueMap[assignment.id] ?? 0}
						commsMatrixStats={commsMatrixStatsMap[assignment.id]}
						voteCounts={voteCountsMap[assignment.id]}
						reminderDueCount={reminderDueMap[assignment.id] ?? 0}
						jobylonBoardUrl={
							jobylonByAssignment.get(assignment.id)?.board_url
						}
					/>
				);
			})}
		</Box>
	);
};

export const RecruitAssignments = ({
	title,
	activeUser,
	readOnly = false,
}: {
	title: React.ReactNode;
	activeUser: User;
	readOnly?: boolean;
}) => {
	const [statusFilter, setStatusFilter] = useQueryState(
		"statusFilter",
		parseAsString.withDefault(DEFAULT_ASSIGNMENT_STATUS_FILTER)
	);
	const [assignmentIdParam, setAssignmentIdParam] = useQueryState(
		"assignmentId",
		parseAsInteger
	);
	const [viewType, setViewType] = useQueryState(
		"viewtype",
		parseAsString.withDefault("card")
	);
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [runningTasks, setRunningTasks] = useState<RunningTasksMap | null>(null);
	const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(
		null
	);
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [llmQueueStats, setLlmQueueStats] = useState<AssignmentLlmQueueStat[]>([]);
	const [commsMatrixStatsMap, setCommsMatrixStatsMap] = useState<
		Record<number, CommsMatrixStats>
	>({});
	const [voteCountsMap, setVoteCountsMap] = useState<
		Record<number, AssignmentVoteCounts>
	>({});
	// One call answers every card: the due list spans all this user's roles and
	// each row names its own, so the badges are a group-by rather than N requests.
	const [reminderDueMap, setReminderDueMap] = useState<Record<number, number>>({});
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [prefillDescription, setPrefillDescription] = useState<string | null>(null);
	const jobylonByAssignment = useJobylonLinkedJobs();

	// Linking a Jobylon job writes the ad's URL and deadline onto the
	// assignment after it was created, so the object this list got back from
	// the create is already stale. Reload that one row when the link lands.
	useEffect(() => {
		const onLinked = (event: Event) => {
			const { assignmentId } = (event as CustomEvent<JobylonLinkChangedDetail>)
				.detail;
			getAssignmentById(assignmentId).then((fresh) => {
				setAssignments((prev) =>
					prev.map((a) => (a.id === fresh.id ? fresh : a))
				);
			});
		};
		window.addEventListener(JOBYLON_LINK_CHANGED_EVENT, onLinked);
		return () => window.removeEventListener(JOBYLON_LINK_CHANGED_EVENT, onLinked);
	}, []);
	const fetchIdRef = useRef(0);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchorEl);
	const id = open ? "filter-menu" : undefined;
	const theme = useTheme();
	const statusChoices = useStatusChoices();

	const activeStatuses = statusFilter ? statusFilter.split(",") : [];

	useEffect(() => {
		if (activeStatuses.length === 0) {
			setAssignments([]);
			return;
		}

		const statusValues = statusKeysToValues(activeStatuses);
		if (statusValues.length === 0) {
			setAssignments([]);
			return;
		}

		const requestId = ++fetchIdRef.current;

		Promise.all([
			getAssignments(undefined, { statusValues, userId: activeUser.id }),
			getRunningTasksByAssignment([
				"core.tasks.rebuild_gcq",
				"core.tasks.fill_llm_queue",
			]),
		])
			.then(([assignmentsRes, tasksRes]) => {
				if (requestId !== fetchIdRef.current) return;

				// Sort by company_name with id as stable tiebreaker
				const sorted = [...assignmentsRes].sort((a, b) => {
					const nameA = a.company_name ?? "";
					const nameB = b.company_name ?? "";
					if (nameA !== nameB) return nameA > nameB ? 1 : -1;
					return a.id - b.id;
				});
				setAssignments(sorted);
				setRunningTasks(tasksRes);

				if (assignmentIdParam !== null) {
					const found = sorted.find((el) => el.id === assignmentIdParam);
					if (found) setSelectedAssignment(found);
				}
			})
			// A deploy replaces gunicorn for ~30-90s and the proxy answers 502 to
			// everything in flight. Keep whatever is on screen; the 60s poll below
			// picks the state back up on its next tick.
			.catch(() => {});
	}, [statusFilter, activeUser.id]);

	const fetchLlmQueueStats = useCallback(() => {
		if (assignments.length === 0) return;
		const assignmentIds = assignments.map((a) => a.id);
		getAssignmentLlmQueueStats(assignmentIds)
			.then(setLlmQueueStats)
			.catch(() => {});
	}, [assignments]);

	useEffect(() => {
		if (assignments.length === 0) {
			setLlmQueueStats([]);
			return;
		}
		fetchLlmQueueStats();
	}, [fetchLlmQueueStats]);

	useEffect(() => {
		if (assignments.length === 0) {
			setCommsMatrixStatsMap({});
			setVoteCountsMap({});
			return;
		}
		const ids = assignments.map((a) => a.id);
		getCommsMatrixStatsBulk(ids)
			.then(setCommsMatrixStatsMap)
			.catch(() => {});
		getAssignmentVoteCountsBulk(ids)
			.then(setVoteCountsMap)
			.catch(() => {});
	}, [assignments]);

	useEffect(() => {
		fetchDueReminders({ userId: activeUser.id })
			.then((rows) => setReminderDueMap(countDueRemindersByAssignment(rows)))
			.catch(() => setReminderDueMap({}));
	}, [activeUser.id]);

	const fetchAssignmentTasks = () => {
		getRunningTasksByAssignment([
			"core.tasks.rebuild_gcq",
			"core.tasks.fill_llm_queue",
		])
			.then(setRunningTasks)
			.catch(() => {});
	};

	useEffect(() => {
		const interval = setInterval(() => {
			fetchAssignmentTasks();
			fetchLlmQueueStats();
		}, 60 * 1000);
		return () => clearInterval(interval);
	}, [fetchLlmQueueStats]);

	useRefreshHotkey(
		useCallback(() => {
			fetchAssignmentTasks();
			fetchLlmQueueStats();
		}, [fetchLlmQueueStats])
	);

	const replaceAssignment = useCallback((updated: Assignment) => {
		setAssignments((prev) =>
			prev.map((el) => (el.id === updated.id ? updated : el))
		);
	}, []);

	// The card's job-ad mark comes off the assignment row, so writing or
	// publishing an ad only shows up there once that row is refetched.
	useEffect(() => {
		const onJobAdChanged = (event: Event) => {
			const { assignmentId } = (event as CustomEvent<JobAdChangedDetail>).detail;
			getAssignmentById(assignmentId).then(replaceAssignment);
		};
		window.addEventListener(JOB_AD_CHANGED_EVENT, onJobAdChanged);
		return () => window.removeEventListener(JOB_AD_CHANGED_EVENT, onJobAdChanged);
	}, [replaceAssignment]);

	const handleFilter = (status: string) => {
		const current = activeStatuses;
		let newStatuses: string[];
		if (current.includes(status)) {
			newStatuses = current.filter((st) => st !== status);
		} else {
			newStatuses = [...current, status];
		}
		setStatusFilter(newStatuses.length > 0 ? newStatuses.join(",") : null);
	};

	const fetchLatestAssignment = (assignment: Assignment) => {
		//Fetch new assignment before editing
		getAssignmentById(assignment.id).then((ass) => {
			replaceAssignment(ass);
			setSelectedAssignment(ass);
		});
	};

	const onSelectAssignment = (assignment: Assignment) => {
		fetchLatestAssignment(assignment);
		setAssignmentIdParam(assignment.id);
	};

	const handleCloseAssignment = () => {
		setAssignmentIdParam(null);
		setSelectedAssignment(null);
		fetchAssignmentTasks();
	};

	const handleUpdateAssignment = (assignment: Assignment) => {
		console.debug({ assignment });

		return patchAssignmentById(assignment.id, assignment).then((res) => {
			replaceAssignment(res);
			return res;
		});
	};

	const handleAddNewAssignment = (assignment: Assignment) => {
		return createAssignment({ ...assignment }).then((res) => {
			setAssignments((prev) => [...prev, res]);
			setOpenAddDialog(false);
			setPrefillDescription(null);
			return res;
		});
	};

	const handleAddRandomAssignment = async () => {
		const response = await getRandomDescription();
		setPrefillDescription(`${response.description}\n<!-- test-${Date.now()} -->`);
		setOpenAddDialog(true);
	};

	const handleCloneAssignment = async (assignment: Assignment) => {
		const newAssignmentName = uniqueName(
			assignment.name,
			assignments.map((el) => el.name)
		);
		const clonedAssignment = await cloneAssignment({
			...assignment,
			name: newAssignmentName,
		});
		setAssignments((prev) => [clonedAssignment, ...prev]);
	};

	const handleDeleteAssignment = (assignment: Assignment) => {
		deleteAssignment(assignment.id).then(() => {
			setAssignments((prev) => prev.filter((el) => el.id !== assignment.id));
		});
	};

	const handleGoToBoard = (assignment: Assignment) => {
		window.open(`board/v2?assignmentId=${assignment.id}`, "_blank");
	};

	const handleOpenFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleCloseFilterMenu = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Grid size={12} data-component="main-container">
				<Grid container spacing={1} data-component="content-wrapper">
					<Grid size={12} data-component="card-container">
						<Card data-component="main-card">
							<Stack
								data-component="header-stack"
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								p={2}
							>
								<Typography variant="h6" data-component="title">
									{title}
								</Typography>
								<Stack direction="row" data-component="header-actions">
									<IconButton
										data-component="view-card-button"
										disabled={viewType === "card"}
										onClick={() => setViewType("card")}
									>
										<GridView />
									</IconButton>
									<IconButton
										data-component="view-list-button"
										disabled={viewType === "list"}
										onClick={() => setViewType("list")}
									>
										<FormatListBulleted />
									</IconButton>
									<IconButton
										onClick={handleOpenFilterMenu}
										data-component="filter-button"
									>
										{<FilterListIcon />}
									</IconButton>
								</Stack>
								<Menu
									data-component="filter-menu"
									id={id}
									open={open}
									anchorEl={anchorEl}
									onClose={handleCloseFilterMenu}
									anchorOrigin={{
										vertical: "bottom",
										horizontal: "left",
									}}
								>
									<Box p={2} data-component="filter-content">
										<Typography data-component="filter-title">
											Filter Status
										</Typography>
										<MenuList
											sx={{ maxWidth: 250 }}
											data-component="filter-menu-list"
										>
											<Grid
												container
												spacing={1}
												data-component="filter-chips-grid"
											>
												{statusChoices.map((choice) => {
													const isActive =
														activeStatuses.includes(
															choice.key
														);
													const chipColor =
														theme.assignmentStatus[
															statusToThemeKey[choice.key]
														];
													return (
														<Grid
															data-component="filter-chip-grid"
															key={`filter-button-${choice.key}`}
														>
															<Chip
																data-component="filter-chip"
																label={choice.name}
																sx={{
																	backgroundColor:
																		isActive
																			? chipColor
																			: null,
																	color: isActive
																		? "common.black"
																		: null,
																	"&.MuiChip-root:hover":
																		{
																			backgroundColor:
																				isActive
																					? chipColor
																					: null,
																			color: isActive
																				? "common.black"
																				: null,
																		},
																}}
																size="small"
																variant="outlined"
																onClick={() =>
																	handleFilter(
																		choice.key
																	)
																}
															/>
														</Grid>
													);
												})}
											</Grid>
										</MenuList>
									</Box>
								</Menu>
							</Stack>
							{viewType === "list" ? (
								<Box p={2}>
									<AssignmentsList
										id="todo-assignments-list"
										assignments={assignments}
										onClickAssignment={onSelectAssignment}
										onCloneAssignment={handleCloneAssignment}
										onDeleteAssignment={handleDeleteAssignment}
										onGoToBoard={handleGoToBoard}
										jobylonByAssignment={jobylonByAssignment}
									/>
								</Box>
							) : (
								<RecruitmentAssignmentList
									assignments={assignments}
									readOnly={readOnly}
									activeUser={activeUser}
									runningTasks={runningTasks}
									onTasksChanged={fetchAssignmentTasks}
									onSelectAssignment={onSelectAssignment}
									onAddAssignment={() => setOpenAddDialog(true)}
									onAddRandomAssignment={handleAddRandomAssignment}
									llmQueueStats={llmQueueStats}
									commsMatrixStatsMap={commsMatrixStatsMap}
									voteCountsMap={voteCountsMap}
									reminderDueMap={reminderDueMap}
									jobylonByAssignment={jobylonByAssignment}
								/>
							)}
						</Card>
					</Grid>
				</Grid>
			</Grid>
			{selectedAssignment !== null ? (
				<AssignmentViewModal
					open={selectedAssignment !== null}
					assignment={selectedAssignment}
					assignments={assignments}
					onSave={(assignment) => handleUpdateAssignment(assignment)}
					onClose={handleCloseAssignment}
				/>
			) : null}
			<AssignmentAddDialog
				open={openAddDialog}
				onClose={() => {
					setOpenAddDialog(false);
					setPrefillDescription(null);
				}}
				onSave={(data) => handleAddNewAssignment(data)}
				initialDescription={prefillDescription ?? undefined}
			/>
			<Snackbar
				data-component="notification-snackbar"
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={snackbarMessage !== ""}
				onClose={() => setSnackbarMessage("")}
				autoHideDuration={6000}
			>
				<Alert
					data-component="notification-alert"
					onClose={() => setSnackbarMessage("")}
					severity="success"
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
